import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Alert } from '@mui/material';
import axios from '../../../axios';
import './Contact.scss';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { getApiErrorMessage } from '../../../utils/notify';
import { emailSchema } from '../../../utils/validation';

const validationSchema = Yup.object({
    email: emailSchema,
    title: Yup.string().max(100, 'Tytuł zapytania nie może mieć więcej niż 100 znaków').required('Tytuł zapytania jest wymagany'),
    message: Yup.string()
        .max(500, 'Treść zapytania nie może mieć więcej niż 500 znaków')
        .required('Treść zapytania jest wymagana'),
});

const DEFAULT_TITLE = (
    <>
        Porozmawiajmy
        <em> o Twoim zakładzie</em>
    </>
);

const DEFAULT_LEAD =
    'Masz pytania o Nekrodruk, szablony albo sprzęt do tabliczek? Napisz – odpowiemy najpóźniej w ciągu jednego dnia roboczego.';

const DEFAULT_POINTS = [
    { title: 'Dobór sprzętu', text: 'Podpowiemy, jaka drukarka i prasa termiczna sprawdzą się u Ciebie.' },
    { title: 'Szablony dla zakładu', text: 'Przygotujemy ramki z nazwą zakładu i kształty tabliczek, których używasz.' },
    { title: 'Pomoc we wdrożeniu', text: 'Pokażemy, jak szybko zacząć pracę w programie.' },
];

/**
 * Formularz kontaktowy. Teksty po lewej można podmienić (np. w poradnikach), domyślnie są te ze strony głównej.
 * @param {{ title?: React.ReactNode, lead?: string, points?: {title: string, text: string}[], defaultSubject?: string }} props
 */
const Contact = ({ title = DEFAULT_TITLE, lead = DEFAULT_LEAD, points = DEFAULT_POINTS, defaultSubject = '' }) => {
    const initialValues = { name: '', surname: '', email: '', title: defaultSubject, message: '' };
    const [status, setStatus] = useState({ type: '', message: '' });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        setStatus({ type: '', message: '' });

        // Najpierw zapis wiadomości (to ona trafia do panelu admina), potem powiadomienie mailowe.
        // Gdy zapis się uda, a mail nie, wiadomość i tak do nas dotarła – nie każemy wysyłać jej ponownie.
        try {
            await axios.post('/message', values);
        } catch (error) {
            console.error('Wystąpił błąd podczas wysyłania wiadomości:', error);
            setStatus({
                type: 'error',
                message: getApiErrorMessage(error, 'Nie udało się wysłać wiadomości. Spróbuj ponownie lub napisz na nekrodruk@gmail.com.'),
            });
            setSubmitting(false);
            return;
        }

        try {
            await axios.post('/sendEmail', values);
        } catch (error) {
            console.error('Nie udało się wysłać powiadomienia e-mail:', error);
        }

        setStatus({ type: 'success', message: 'Wiadomość została wysłana. Odpowiemy najpóźniej w ciągu jednego dnia roboczego.' });
        resetForm();
        setSubmitting(false);
    };

    return (
        <section className='Contact' id='Contact'>
            <div className='Contact_wrapper'>
                <div className='Contact_container'>
                    <div className='Contact_intro'>
                        <span className='Contact_kicker'>Kontakt</span>
                        <h2 className='Contact_title'>{title}</h2>
                        <p className='Contact_lead'>{lead}</p>
                        <ul className='Contact_points'>
                            {points.map((point) => (
                                <li key={point.title}>
                                    <strong>{point.title}</strong>
                                    <span>{point.text}</span>
                                </li>
                            ))}
                        </ul>
                        <a className='Contact_link' href='mailto:nekrodruk@gmail.com'>
                            <EmailOutlinedIcon fontSize='small' aria-hidden='true' />
                            nekrodruk@gmail.com
                        </a>
                    </div>

                    <div className='Contact_formCard'>
                        <h3 className='Contact_formTitle'>Wyślij wiadomość</h3>
                        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                            {({ isSubmitting }) => (
                                <Form className='Contact_form' noValidate>
                                    <div className='Contact_fieldGroup'>
                                        <label className='Contact_label' htmlFor='email'>
                                            Adres e-mail
                                        </label>
                                        <Field type='email' id='email' name='email' className='Contact_input' placeholder='np. kontakt@twojafirma.pl' />
                                        <ErrorMessage name='email' component='div' className='Contact_error' />
                                    </div>

                                    <div className='Contact_fieldGroup'>
                                        <label className='Contact_label' htmlFor='title'>
                                            Tytuł zapytania
                                        </label>
                                        <Field type='text' id='title' name='title' className='Contact_input' placeholder='Jak możemy pomóc?' />
                                        <ErrorMessage name='title' component='div' className='Contact_error' />
                                    </div>

                                    <div className='Contact_fieldGroup'>
                                        <label className='Contact_label' htmlFor='message'>
                                            Treść zapytania
                                        </label>
                                        <Field
                                            as='textarea'
                                            id='message'
                                            name='message'
                                            className='Contact_textarea'
                                            placeholder='Opisz krótko swoją sprawę...'
                                        />
                                        <ErrorMessage name='message' component='div' className='Contact_error' />
                                    </div>

                                    <div className='Contact_formFooter'>
                                        <button className='Contact_submit' type='submit' disabled={isSubmitting}>
                                            {isSubmitting ? 'Wysyłanie…' : 'Wyślij wiadomość'}
                                        </button>
                                        <div role='status' aria-live='polite'>
                                            {status.message && (
                                                <Alert severity={status.type === 'success' ? 'success' : 'error'}>
                                                    {status.message}
                                                </Alert>
                                            )}
                                        </div>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
